import React from "react";
import { Box, Button, Container, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const RANGER_QUOTES = [
  {
    title: "10-404: PAGE NOT FOUND",
    message: "This road don't lead nowhere, partner. Looks like you've driven off the map.",
    subtext: "Even GPS can't find this one.",
  },
  {
    title: "ROAD CLOSED AHEAD",
    message: "Whoa there, cowboy! This page has been cleared from the highway.",
    subtext: "No debris here... just empty pavement.",
  },
  {
    title: "UNIT MISSING",
    message: "We've dispatched a search party, but this page is more lost than a motorist without Waze.",
    subtext: "Last seen: Never. Because it doesn't exist.",
  },
  {
    title: "STRANDED MOTORIST",
    message: "This page ran out of gas somewhere on US-75 and we can't locate it.",
    subtext: "Unlike you, we can't assist this one.",
  },
  {
    title: "WRONG EXIT",
    message: "You took the wrong exit, partner. This ain't the page you're looking for.",
    subtext: "Make a U-turn when legally permitted.",
  },
];

export default function NotFound() {
  const navigate = useNavigate();
  const quote = RANGER_QUOTES[Math.floor(Math.random() * RANGER_QUOTES.length)];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            textAlign: "center",
            background: "#ffffff",
            borderRadius: 3,
            border: "4px solid",
            borderColor: "warning.main",
          }}
        >
          <WarningAmberIcon
            sx={{
              fontSize: 80,
              color: "warning.main",
              mb: 2,
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.7, transform: "scale(1.1)" },
                "100%": { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
          
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "3rem", sm: "4rem" },
              fontWeight: 900,
              color: "error.main",
              textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
              mb: 1,
            }}
          >
            404
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "warning.dark",
              textTransform: "uppercase",
              letterSpacing: 2,
              mb: 3,
            }}
          >
            {quote.title}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "#1a1a1a",
              mb: 2,
              fontStyle: "italic",
            }}
          >
            "{quote.message}"
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#555555",
              mb: 4,
            }}
          >
            {quote.subtext}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate("/dashboard")}
              sx={{
                textTransform: "uppercase",
                fontWeight: 700,
                px: 4,
              }}
            >
              🚗 Return to Dashboard
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => navigate(-1)}
              sx={{
                textTransform: "uppercase",
                fontWeight: 700,
                px: 4,
              }}
            >
              ↩️ Go Back
            </Button>
          </Box>

          <Box sx={{ mt: 4, pt: 3, borderTop: "1px dashed", borderColor: "#cccccc" }}>
            <Typography
              variant="caption"
              sx={{
                color: "#888888",
                display: "block",
              }}
            >
              🤠 Texas Highway Rangers - Keeping the roads safe since... well, you get it.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#888888",
                fontFamily: "monospace",
                mt: 1,
                display: "block",
              }}
            >
              Error Code: PAGE_WENT_10-7
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
